import React, { useCallback, useEffect, useState } from 'react';
import moment from 'moment';
import Api from 'services/api';
import { Container } from './styles';

interface PerfilItem {
  descricao: string;
  ocorrencias: number;
  ufs: string[];
  ultima_data?: string | null;
  origem?: string | null;
}

interface PerfilResponse {
  ativo: boolean;
  status?: string;
  segmento?: string | null;
  familias?: string[];
  ufs?: string[];
  total_itens: number;
  pagina: number;
  por_pagina: number;
  atualizado_em?: string | null;
  perfil_fraco?: 'poucos_itens' | 'sem_nucleo' | 'sem_segmento' | null;
  itens: PerfilItem[];
}

const AVISO_PERFIL_FRACO = {
  poucos_itens:
    'Seu perfil ainda tem poucos itens, e o Match pode deixar licitações boas de fora. Descreva no "Personalizar" os produtos e serviços que sua empresa fornece.',
  sem_nucleo:
    'Seu perfil mistura itens que apareceram uma vez só, sem um produto principal. Exclua o que não faz parte do seu negócio e descreva no "Personalizar" o que você fornece.',
  sem_segmento:
    'Ainda não identificamos o segmento da sua empresa. Descreva no "Personalizar" o que você fornece para o Match acertar mais.',
};

const UFS_BRASIL = [
  'AC',
  'AL',
  'AM',
  'AP',
  'BA',
  'CE',
  'DF',
  'ES',
  'GO',
  'MA',
  'MG',
  'MS',
  'MT',
  'PA',
  'PB',
  'PE',
  'PI',
  'PR',
  'RJ',
  'RN',
  'RO',
  'RR',
  'RS',
  'SC',
  'SE',
  'SP',
  'TO',
];

const MatchPerfil: React.FC = () => {
  const [perfil, setPerfil] = useState<PerfilResponse | null>(null);
  const [pagina, setPagina] = useState(1);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(false);
  const [salvando, setSalvando] = useState<string | null>(null);
  const [editando, setEditando] = useState<string | null>(null);
  const [textoEdicao, setTextoEdicao] = useState('');
  const [editandoUfs, setEditandoUfs] = useState(false);
  const [ufsRascunho, setUfsRascunho] = useState<string[]>([]);

  const carregar = useCallback((page: number) => {
    setLoading(true);
    setErro(false);
    Api.get(`boletim_web/public/match/perfil?page=${page}`)
      .then((res) => setPerfil(res.data))
      .catch(() => setErro(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => carregar(pagina), [carregar, pagina]);

  const recarregarApos = useCallback(
    (restantes: number) => {
      const aindaCabe = restantes > 0 || pagina === 1;
      if (aindaCabe) carregar(pagina);
      else setPagina((p) => Math.max(1, p - 1));
    },
    [carregar, pagina],
  );

  const excluir = useCallback(
    (descricao: string) => {
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Excluir "${descricao}" do seu perfil?`)) return;
      const restantesNaPagina = (perfil?.itens.length ?? 1) - 1;
      setSalvando(descricao);
      Api.delete('boletim_web/public/match/item', { data: { descricao } })
        .then(() => recarregarApos(restantesNaPagina))
        // eslint-disable-next-line no-alert
        .catch(() => alert('Não foi possível excluir o item. Tente novamente.'))
        .finally(() => setSalvando(null));
    },
    [recarregarApos, perfil],
  );

  const salvarEdicao = useCallback(
    (descricao: string) => {
      const nova = textoEdicao.trim();
      if (!nova || nova === descricao) {
        setEditando(null);
        return;
      }

      setSalvando(descricao);
      Api.patch('boletim_web/public/match/item', { descricao, descricao_nova: nova })
        .then(() => {
          setEditando(null);
          carregar(pagina);
        })
        // eslint-disable-next-line no-alert
        .catch(() => alert('Não foi possível salvar o item. Tente novamente.'))
        .finally(() => setSalvando(null));
    },
    [textoEdicao, carregar, pagina],
  );

  const salvarUfs = useCallback(() => {
    setSalvando('__ufs__');
    Api.put('boletim_web/public/match/ufs', { ufs: ufsRascunho })
      .then((res) => {
        setPerfil((p) => (p ? { ...p, ufs: res.data?.ufs || ufsRascunho } : p));
        setEditandoUfs(false);
      })
      // eslint-disable-next-line no-alert
      .catch(() => alert('Não foi possível salvar as regiões. Tente novamente.'))
      .finally(() => setSalvando(null));
  }, [ufsRascunho]);

  if (loading && !perfil) return <Container>Carregando seu perfil…</Container>;

  if (erro) {
    return (
      <Container>
        <p className="perfil-vazio">
          Não foi possível carregar seu perfil agora.
          <button type="button" onClick={() => carregar(pagina)}>
            Tentar de novo
          </button>
        </p>
      </Container>
    );
  }

  if (!perfil || perfil.total_itens === 0) {
    return (
      <Container>
        <p className="perfil-vazio">
          Ainda não temos histórico suficiente para montar seu perfil. Descreva no campo
          &quot;Personalizar&quot; o que sua empresa fornece e usamos isso no lugar.
        </p>
      </Container>
    );
  }

  const totalPaginas = Math.max(1, Math.ceil(perfil.total_itens / perfil.por_pagina));
  const primeiro = (perfil.pagina - 1) * perfil.por_pagina + 1;
  const ultimo = Math.min(perfil.pagina * perfil.por_pagina, perfil.total_itens);

  return (
    <Container>
      <p className="perfil-intro">
        Este é o perfil que montamos a partir das licitações que sua empresa já disputou. É ele que
        usamos para escolher as licitações compatíveis no seu boletim.
      </p>

      {perfil.ativo && perfil.perfil_fraco && AVISO_PERFIL_FRACO[perfil.perfil_fraco] && (
        <p className="perfil-aviso">{AVISO_PERFIL_FRACO[perfil.perfil_fraco]}</p>
      )}

      {!perfil.ativo && (
        <p className="perfil-aviso">
          Seu perfil ainda não está ativo no match. Suas alterações ficam salvas e passam a valer
          quando ele for ativado.
        </p>
      )}

      {perfil.segmento && (
        <section className="perfil-bloco">
          <h4>Seu segmento</h4>
          <p className="perfil-segmento">{perfil.segmento}</p>
          {!!perfil.familias?.length && (
            <div className="perfil-chips">
              {perfil.familias.map((f) => (
                <span className="perfil-chip" key={f}>
                  {f}
                </span>
              ))}
            </div>
          )}
        </section>
      )}

      <section className="perfil-bloco">
        <h4>
          Estados onde você atua
          {!editandoUfs && (
            <button
              type="button"
              className="perfil-link"
              onClick={() => {
                setUfsRascunho(perfil.ufs || []);
                setEditandoUfs(true);
              }}
            >
              Editar
            </button>
          )}
        </h4>

        {editandoUfs ? (
          <>
            <div className="perfil-chips perfil-chips--seletor">
              {UFS_BRASIL.map((uf) => {
                const ativa = ufsRascunho.includes(uf);
                return (
                  <button
                    type="button"
                    key={uf}
                    className={ativa ? 'perfil-uf-opt perfil-uf-opt--on' : 'perfil-uf-opt'}
                    onClick={() =>
                      setUfsRascunho((atual) =>
                        ativa ? atual.filter((u) => u !== uf) : [...atual, uf],
                      )
                    }
                  >
                    {uf}
                  </button>
                );
              })}
            </div>
            <div className="perfil-acoes-linha">
              <button
                type="button"
                className="perfil-btn-salvar"
                onClick={salvarUfs}
                disabled={salvando === '__ufs__'}
              >
                {salvando === '__ufs__' ? 'Salvando…' : 'Salvar regiões'}
              </button>
              <button type="button" className="perfil-link" onClick={() => setEditandoUfs(false)}>
                Cancelar
              </button>
            </div>
          </>
        ) : (
          <div className="perfil-chips">
            {(perfil.ufs || []).map((uf) => (
              <span className="perfil-chip perfil-chip--uf" key={uf}>
                {uf}
              </span>
            ))}
            {!perfil.ufs?.length && (
              <span className="perfil-segmento">Nenhuma região definida.</span>
            )}
          </div>
        )}
      </section>

      <section className="perfil-bloco">
        <h4>
          Itens que você fornece
          <span className="perfil-contagem">{perfil.total_itens}</span>
        </h4>

        <ul className="perfil-itens">
          {perfil.itens.map((item) => (
            <li key={`${item.descricao}-${item.ocorrencias}`}>
              {editando === item.descricao ? (
                <div className="perfil-item-edicao">
                  <input
                    type="text"
                    value={textoEdicao}
                    onChange={(e) => setTextoEdicao(e.target.value)}
                    disabled={salvando === item.descricao}
                  />
                  <button
                    type="button"
                    className="perfil-btn-salvar"
                    onClick={() => salvarEdicao(item.descricao)}
                    disabled={salvando === item.descricao}
                  >
                    {salvando === item.descricao ? 'Salvando…' : 'Salvar'}
                  </button>
                  <button type="button" className="perfil-link" onClick={() => setEditando(null)}>
                    Cancelar
                  </button>
                </div>
              ) : (
                <>
                  <span className="perfil-item-desc">{item.descricao}</span>
                  <span className="perfil-item-meta">
                    {item.ocorrencias > 1 ? `${item.ocorrencias}×` : ''}
                    {item.origem === 'declarado' && <em>informado por você</em>}
                    <button
                      type="button"
                      className="perfil-item-acao"
                      title="Editar item"
                      onClick={() => {
                        setEditando(item.descricao);
                        setTextoEdicao(item.descricao);
                      }}
                    >
                      <span className="material-icons">edit</span>
                    </button>
                    <button
                      type="button"
                      className="perfil-item-acao perfil-item-acao--excluir"
                      title="Excluir item"
                      disabled={salvando === item.descricao}
                      onClick={() => excluir(item.descricao)}
                    >
                      <span className="material-icons">delete_outline</span>
                    </button>
                  </span>
                </>
              )}
            </li>
          ))}
        </ul>

        {totalPaginas > 1 && (
          <div className="perfil-paginacao">
            <button
              type="button"
              onClick={() => setPagina((p) => Math.max(1, p - 1))}
              disabled={perfil.pagina <= 1 || loading}
            >
              Anterior
            </button>
            <span>
              {primeiro}–{ultimo} de {perfil.total_itens}
            </span>
            <button
              type="button"
              onClick={() => setPagina((p) => Math.min(totalPaginas, p + 1))}
              disabled={perfil.pagina >= totalPaginas || loading}
            >
              Próxima
            </button>
          </div>
        )}
      </section>

      {perfil.atualizado_em && (
        <p className="perfil-rodape">
          Perfil atualizado em {moment(perfil.atualizado_em).format('DD/MM/YYYY')}. As alterações
          valem a partir do próximo boletim.
        </p>
      )}
    </Container>
  );
};

export default MatchPerfil;
